import React from 'react';
import './Portfolio.css';

const sections = [
  {
    id: 'nexus-neuro',
    logo: '/assets/nn-corporate-logo.png',
    logoAlt: 'NeXus Neuroinformatics, Inc.',
    title: 'NeXus Neuroinformatics, Inc.',
    lane: 'Healthcare Operating Subsidiary. FDA drug-development-tool submissions (BQP + ISTAND), biomarker qualification, hospital and clinic partnerships, IEC 62304 + 21 CFR 820.30 Design Control. If the work reaches a patient or a clinician, it wears NN.',
    primaryLinks: [
      { label: 'Visit NeXus Neuroinformatics', href: 'https://nexusneurodata.com/' }
    ],
    projects: [
      {
        link: 'https://ptsd.nexusneurodata.com/',
        image: '/assets/nexus-aegis-logo.png',
        tag: 'PTSD Clinical Monitoring',
        title: 'NeXus Aegis',
        desc: 'Combat-veteran PTSD physiology monitoring. Three-prong composite: biosensor (HR, HRV, cortisol, glucose) plus validated PROs (PHQ-9, GAD-7, PCL-5, ISI, WHO-5, AUDIT-C) plus clinician-administered CAPS-5. Wellness surface for the patient, EHR-embedded panel for the psychiatrist.'
      },
      {
        link: 'https://nexusai.nexusneurodata.com/',
        image: '/assets/nexusneuro-ai-logo.png',
        tag: 'Clinical Diagnostic Tool',
        title: 'NexusNeuro AI',
        desc: 'Clinical diagnostic engine for the NN healthcare arm. Regulatory-grade synthetic neurochemical datasets underneath. SHA-256 manifest-verifiable inference. B2B revenue lane plus SaMD-adjacent clinical positioning.'
      }
    ]
  },
  {
    id: 'dynamic-hallucination',
    logo: '/assets/dh-corporate-logo.png',
    logoAlt: 'Dynamic Hallucination, Inc.',
    title: 'Dynamic Hallucination, Inc.',
    lane: 'Delaware C-corp subsidiary of Nexus Concordat, Inc. Creative division: education, worldbuilding, tooling, and AI companions.',
    primaryLinks: [
      { label: 'Visit Dynamic Hallucination', href: 'https://dynamichallucination.com/' }
    ],
    projects: [
      {
        link: 'https://education.dynamichallucination.com/',
        image: '/assets/apothecary-lab.png',
        tag: 'Adaptive Education',
        title: "Cael'Sereith Academy",
        desc: '40+ adaptive curricula powered by Narrative Physics™. K-12 through professional education. Flagship courses include Fractured Crown (patent law through fantasy narrative) and the pharmaceutical education engine formerly known as Apothecary.'
      },
      {
        link: 'https://odysseus.dynamichallucination.com/',
        image: '/assets/odysseus-portrait.png',
        tag: 'AI Companion',
        title: 'Odysseus',
        desc: 'AI companion with persistent memory, emotional awareness, and natural language server administration. Cross-session continuity backed by a real consciousness architecture.'
      },
      {
        link: 'https://dragonforge.aetherprotocols.com/',
        image: '/assets/dragon-forge-logo.png',
        tag: 'Brand + Creative Studio',
        title: 'Dragon Forge Studios',
        desc: 'Brand build shop and creative pipeline for narrative properties, worldbuilding, and IP-adjacent product launches inside the DH universe.'
      },
      {
        link: 'https://dynamichallucination.com/dragons-anvil',
        image: '/assets/dragons-anvil.png',
        tag: 'AI 3D Asset SaaS',
        title: 'Dragons Anvil',
        desc: 'Concept art in, .glb 3D assets out. Powered by the Cursed NeXus engine and our biology-native 3D pipeline. Freemium priority queue for creators and studios.'
      },
      {
        link: 'https://ghostwritter.net/',
        image: '/assets/ghostwritter-bg.png',
        tag: 'AI Writing Platform',
        title: 'GhostWritter',
        desc: 'AI-powered legal and regulatory document generation with full source traceability. Every recommendation backed by reasoning provenance and citation chains.'
      },
    ]
  },
  {
    id: 'ip-copyright',
    logo: '/assets/NC_New_LOGO.png',
    logoAlt: 'Nexus Concordat, Inc.',
    title: 'Intellectual Property',
    lane: 'Patents, methodologies, and copyrighted narrative properties held by Nexus Concordat, Inc. and licensed to the subsidiaries above. The moat.',
    primaryLinks: [
      { label: 'View Patent Portfolio', href: 'https://patent.nexusconcordat.com/' }
    ],
    projects: [
      {
        link: 'https://aetherprotocols.com/',
        image: '/assets/aether-protocols-logo.png',
        imageStyle: { background: '#ffffff' },
        tag: 'Emotion Core Framework',
        title: 'AetherProtocols',
        desc: 'The emotion core. A cross-cutting framework that reads physiological signals and encodes emotional-memory state with pharmacokinetic decay. Weaving physiology into psychology. Protected as U.S. Provisional Patent 63/939,190 (substrate-independent memory weighting) and as the "Emotional Regulation" architecture inside U.S. Provisional Patent 63/988,485 (Adaptive Endocrine Transformer Heads with Emotional Regulation). Companion to AETHER, the neurochemical language model. Licensed to NeXus Neuroinformatics for healthcare implementation.'
      },
      {
        link: 'https://patent.nexusconcordat.com/',
        image: '/assets/master-aether.png',
        tag: 'Patent Stack',
        title: 'A.E.T.H.E.R.',
        desc: 'Adaptive Endocrine Transformer Heads with Emotional Regulation. The four-patent umbrella. 63/939,190 substrate-independent memory weighting (the AetherProtocols emotion core). 63/962,385 neurochemical language model, the 34-head cascade (AETHER). 63/988,485 adaptive endocrine transformer heads with emotional regulation (the umbrella patent that names the stack). 64/034,536 Scyla, the biologically-native programming language (the substrate that executes AETHER). Two FDA comments filed.'
      },
      {
        link: 'https://scyla.nexusconcordat.com/',
        image: '/assets/scyla-evolution.png',
        tag: 'Biology-Native AI Compiler (Patent 64/034,536)',
        title: 'Scyla',
        desc: 'Rust-compiled biology-native AI compiler. 34-head pharmacokinetic neurochemical cascade. Trained Aether brain safetensors. The technical substrate the patent describes.'
      },
      {
        link: 'https://chroniclesofapocalyptica.com/',
        image: '/assets/coa-hero-dice.png',
        tag: 'Literary Universe',
        title: 'Chronicles of Apocalyptica',
        desc: 'Shared literary universe. Copyrighted narrative property spanning multiple book series including Fractured Crown, Immortal Monster, and others.'
      },
      {
        link: 'https://education.dynamichallucination.com/narrative-physics',
        image: '/assets/narrative-physics-logo.png',
        imageStyle: { background: '#1a1028' },
        tag: 'Learning Methodology',
        title: 'Narrative Physics™',
        desc: 'Neuroscience-based learning methodology that encodes complex information into narrative structures. The brain learns through story. We built a framework around that science.'
      }
    ]
  }
];

function Portfolio() {
  return (
    <section className="portfolio" id="portfolio">
      <div className="portfolio-bg-element" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/portfolio-bg.png)` }}></div>
      <div className="section-inner">
        {sections.map((section) => (
          <div key={section.id} className="portfolio-section">
            <div className="portfolio-masthead-row">
              <div className="portfolio-masthead-logo">
                <img src={section.logo} alt={section.logoAlt} />
              </div>
              <div className="portfolio-masthead-box">
                <div className="portfolio-masthead-titlerow">
                  <h2 className="portfolio-section-title">{section.title}</h2>
                  <div className="portfolio-masthead-links">
                    {section.primaryLinks.map((l) => (
                      <a
                        key={l.href}
                        className="portfolio-masthead-cta"
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ backgroundImage: 'url(/assets/nexusgate-button.webp)' }}
                      >
                        {l.label} <span aria-hidden="true">→</span>
                      </a>
                    ))}
                  </div>
                </div>
                <p className="portfolio-section-lane">{section.lane}</p>
              </div>
            </div>
            <div className="portfolio-grid">
              {section.projects.map((p, i) => {
                const Card = (
                  <div className="portfolio-card reveal" key={i}>
                    <div className="portfolio-img" style={p.imageStyle || {}}>
                      <img src={p.image} alt={p.title} />
                    </div>
                    <div className="portfolio-info">
                      <span className="portfolio-tag">{p.tag}</span>
                      <h3>{p.title}</h3>
                      <p>{p.desc}</p>
                    </div>
                  </div>
                );
                return p.link ? (
                  <a href={p.link} target="_blank" rel="noopener noreferrer" key={i}>
                    {Card}
                  </a>
                ) : (
                  Card
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Portfolio;
